import {
  addDisplayName,
  addHandlers,
  addState,
  addStateHandlers,
  flowMax,
  addProps,
} from 'ad-hok'
import { FC } from 'react'
import { addApi } from 'shared/src/api'
import {
  CmsUser,
  isMessageRecipientGroup,
  MessageGroup,
  MessageRecipient,
} from 'shared/src/api/types'
import { typedAs } from 'shared/src/utils/typedAs'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { Button } from './Button'
import { ImageField } from './ImageField'
import { Page } from './Page'
import { ResourceSelectField } from './ResourceSelectField'
import { TextAreaNew } from './TextField'

const DEFAULT_RECIPIENT: MessageRecipient = { group: 'featuredCreators' }

const USER_OPTION = 'user'

export const Messaging: FC = flowMax(
  addDisplayName('Messaging'),
  addState('isSending', 'setSending', false),
  addStateHandlers(
    {
      recipient: typedAs<MessageRecipient | null>(DEFAULT_RECIPIENT),
    },
    {
      setUserId: () => (userId: number | undefined) => ({
        recipient: userId !== undefined ? { userId } : null,
      }),
      setGroup: () => (group: MessageGroup | typeof USER_OPTION) => ({
        recipient: group === USER_OPTION ? null : { group },
      }),
      resetRecipient: () => () => ({
        recipient: { group: 'featuredCreators' },
      }),
    }
  ),
  addState('body', 'setBody', ''),
  addState('imageId', 'setImageId', typedAs<number | undefined>(undefined)),
  addApi,
  addTranslationHelpers,
  addProps(({ recipient }) => ({
    selectValue:
      recipient === null
        ? USER_OPTION
        : isMessageRecipientGroup(recipient)
        ? recipient.group
        : USER_OPTION,
    selectedUserId:
      recipient === null
        ? undefined
        : isMessageRecipientGroup(recipient)
        ? undefined
        : recipient.userId,
  })),
  addHandlers({
    filterUsers:
      () =>
      ({ phoneNumber }: CmsUser): boolean =>
        !!phoneNumber,
    onSend:
      ({
        api,
        setSending,
        recipient,
        resetRecipient,
        body,
        setBody,
        imageId,
        setImageId,
        selectValue,
        selectedUserId,
        t,
      }) =>
      async () => {
        if (!recipient || !body) {
          return
        }

        if (
          window.confirm(
            `${t('admin.messages.alertMessage')} ${selectValue} ${
              selectedUserId ?? ''
            }`
          )
        ) {
          setSending(true)

          try {
            await api.messages.create({
              recipient,
              content: { text: body },
              imageId: imageId ?? null,
            })
            resetRecipient()
            setBody('')
            setImageId(undefined)
            alert(t('admin.messages.successMessage'))
          } catch (error) {
            console.error('Error sending message', error)
            alert(t('admin.messages.errorMessage'))
          } finally {
            setSending(false)
          }
        }
      },
  }),
  ({
    recipient,
    selectValue,
    setGroup,
    setUserId,
    selectedUserId,
    body,
    setBody,
    imageId,
    setImageId,
    onSend,
    isSending,
    filterUsers,
    t,
  }) => (
    <Page>
      <div>
        <select
          value={selectValue}
          onChange={(event) => {
            setGroup(event.target.value as any)
          }}
        >
          <option value={typedAs<MessageGroup>('featuredCreators')}>
            {t('admin.messages.groups.featuredCreators')}
          </option>
          <option value={typedAs<MessageGroup>('creators')}>
            {t('admin.messages.groups.creators')}
          </option>
          <option value={typedAs<MessageGroup>('creatorFundCreators')}>
            {t('admin.messages.groups.creatorFundCreators')}
          </option>
          <option value={USER_OPTION}>{t('admin.messages.user')}</option>
        </select>
        <ResourceSelectField
          label={t('admin.messages.user')}
          resourceName="users"
          selectedId={selectedUserId}
          onChange={setUserId}
          filter={filterUsers}
        />
      </div>
      <TextAreaNew
        label={t('admin.messages.body')}
        value={body}
        onChange={setBody}
      />
      <ImageField
        label={t('admin.messages.image')}
        value={imageId}
        onChange={setImageId}
      />
      <Button
        text={t('admin.messages.send')}
        onClick={onSend}
        disabled={isSending || !recipient || !body}
      />
    </Page>
  )
)
